import { pick, uniq } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { PRICE_PROPERTIES, PRICE_PROPERTIES_LIST, REQUIRED_PRICE_PROPERTIES } from './constants';

export const getIsEqualPrices = (price, currentPrice, ignoreCountry = false) =>
  PRICE_PROPERTIES_LIST.every((key) => {
    return (key === PRICE_PROPERTIES.COUNTRY && ignoreCountry) || price[key] === currentPrice[key];
  });

export const getCopyPrice = (price) => ({
  ...pick(price, PRICE_PROPERTIES_LIST),
  uid: uuidv4(),
});

export const getCopyPrices = (prices = []) => prices.map(getCopyPrice);

export const getActivePrices = (prices = []) =>
  prices.filter(
    (price) =>
      price.active && !price.marketingCampaignId && !price.archived && !price.productRecoId,
  );

export const getIsChanged = (prices = [], currentPrices = []) =>
  prices.length !== currentPrices.length ||
  prices.some((price) => {
    const { currency, country } = price || {};

    const currentPrice = currentPrices.find(
      (curPrice) => curPrice.currency === currency && curPrice.country === country,
    );

    return !currentPrice || !getIsEqualPrices(price, currentPrice);
  });

export const getCurrencies = (currentPrices = []) =>
  uniq(currentPrices.map(({ currency }) => currency)).sort();

export const defaultCountryErrorCheck = (currentPrices = [], currency) => {
  const currencyPrices = currentPrices.filter((currentPrice) => currentPrice.currency === currency);

  return !currencyPrices.some((currencyPrice) => !currencyPrice.country);
};

export const checkRequiredPropertyError = (value) => !(value || value === 0);

export const checkDateRangeError = (price) =>
  price[PRICE_PROPERTIES.END_DATE] &&
  price[PRICE_PROPERTIES.END_DATE] < price[PRICE_PROPERTIES.START_DATE];

export const checkRequiredPricesError = (currentPrices = [], product) =>
  [product?.status, product?.status?.value].includes('ENABLED') && !currentPrices.length;

export const checkDefaultCurrencyError = (product) => !product?.prices?.defaultCurrency;

export const hasPricesErrors = (currentPrices = [], product) => {
  const currencies = getCurrencies(currentPrices);

  return (
    currencies.some((currency) => defaultCountryErrorCheck(currentPrices, currency)) ||
    checkDefaultCurrencyError(product) ||
    checkRequiredPricesError(currentPrices, product) ||
    currentPrices.some(
      (currentPrice) =>
        checkDateRangeError(currentPrice) ||
        REQUIRED_PRICE_PROPERTIES.some((key) => checkRequiredPropertyError(currentPrice[key])),
    )
  );
};

export const getGroupedWithCountryPrices = (prices) => {
  return prices.reduce((acc, cur) => {
    const collapsedGroupIndex = acc.findIndex(([collapsedPrice = {}]) =>
      getIsEqualPrices(collapsedPrice, cur, true),
    );

    collapsedGroupIndex > -1 ? acc[collapsedGroupIndex].push(cur) : acc.push([cur]);

    return acc;
  }, []);
};
